import PropTypes from 'prop-types';
import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, Drawer } from '@mui/material';
import Logo from '../../components/Logo';
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
import palette from '../../theme/palette';
import { PLAYERS_NAV } from 'src/utils/clutchAdminAuth';
import { refreshStoredAdminUser } from 'src/utils/adminApi';
import SidebarAdminPanel from './SidebarAdminPanel';
import { SIDEBAR_WIDTH_EXPANDED, SIDEBAR_WIDTH_MINI } from './sidebarConstants';

const SIDEBAR_BG = palette.drawerBackground;

const RootStyle = styled('div')(({ theme, ownerState }) => ({
  flexShrink: 0,
  width: ownerState.drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  backgroundColor: SIDEBAR_BG
}));

DashboardSidebar.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  drawerWidth: PropTypes.number.isRequired
};

export default function DashboardSidebar({ collapsed, drawerWidth }) {
  const { pathname } = useLocation();
  const mini = collapsed;
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  });

  const syncUser = useCallback((nextUser) => {
    if (nextUser) {
      setUser(nextUser);
    }
  }, []);

  useEffect(() => {
    refreshStoredAdminUser().then(syncUser).catch(() => {});
  }, [syncUser]);

  useEffect(() => {
    try {
      setUser(JSON.parse(localStorage.getItem('user') || 'null'));
    } catch {
      setUser(null);
    }
  }, [pathname]);

  const navItems = PLAYERS_NAV.map((item) => ({
    ...item,
    icon: item.icon
  }));

  const renderContent = (
    <>
      <Box
        sx={{
          flexShrink: 0,
          px: mini ? 1 : 4,
          py: 2,
          pt: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: mini ? 'center' : 'flex-start',
          backgroundColor: SIDEBAR_BG
        }}
      >
        <Logo compact={mini} sx={{ marginLeft: mini ? 0 : 1 }} />
      </Box>

      <Scrollbar
        sx={{
          backgroundColor: SIDEBAR_BG,
          flex: 1,
          minHeight: 0,
          '& .simplebar-content': {
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: SIDEBAR_BG
          },
          '& .simplebar-wrapper, & .simplebar-mask, & .simplebar-offset': {
            backgroundColor: SIDEBAR_BG
          }
        }}
      >
        <NavSection navConfig={navItems} mini={mini} />
      </Scrollbar>

      {user && (
        <Box
          sx={{
            flexShrink: 0,
            mt: 'auto',
            backgroundColor: SIDEBAR_BG,
            borderTop: '1px solid rgba(255,255,255,0.12)'
          }}
        >
          <SidebarAdminPanel user={user} mini={mini} onUserUpdated={syncUser} />
        </Box>
      )}
    </>
  );

  return (
    <RootStyle ownerState={{ drawerWidth }}>
      <Drawer
        open
        variant="permanent"
        PaperProps={{
          sx: {
            width: drawerWidth,
            overflowX: 'hidden',
            bgcolor: SIDEBAR_BG,
            borderRight: '1px dashed',
            borderColor: 'divider',
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen
              })
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: SIDEBAR_BG }}>
          {renderContent}
        </Box>
      </Drawer>
    </RootStyle>
  );
}

export { SIDEBAR_WIDTH_EXPANDED, SIDEBAR_WIDTH_MINI };
